<template>
  <details
    ref="detailsRef"
    class="st-expandable-details"
    :open="isOpen"
    @toggle="handleToggle"
  >
    <summary class="st-expandable-details__summary">
      <StIcon
        :name="isOpen ? 'minus-filled' : 'plus-filled'"
        class="st-expandable-details__icon"
      />
      <component :is="headingTag" class="st-expandable-details__heading">
        {{ headline }}
      </component>
    </summary>
    <div class="st-expandable-details__content">
      <slot />
    </div>
  </details>
</template>

<script setup>
import { ref, watch, computed } from "vue";
import StIcon from "../icon/Icon.vue";

const props = defineProps({
  // The headline text displayed in the summary section
  headline: {
    type: String,
    required: true,
  },

  // Whether the details are initially open
  open: {
    type: Boolean,
    default: false,
  },

  // Heading level for the headline (1-6). Use 'div' to remove semantic heading.
  // Choose the appropriate level based on your page's heading hierarchy.
  headingLevel: {
    type: [Number, String],
    default: "div",
    validator: (value) => {
      const validValues = [1, 2, 3, 4, 5, 6, "div"];
      return validValues.includes(value);
    },
  },
});

const emit = defineEmits(["update:open", "toggle"]);

const detailsRef = ref(null);
const isOpen = ref(props.open);

const headingTag = computed(() => {
  if (props.headingLevel === "div") {
    return "div";
  }
  return `h${props.headingLevel}`;
});

const handleToggle = (event) => {
  // Native details element manages open state, sync with our reactive state
  isOpen.value = event.target.open;
  emit("update:open", isOpen.value);
  emit("toggle", isOpen.value);
};

// Sync prop changes to the native details element
watch(
  () => props.open,
  (newValue) => {
    if (isOpen.value !== newValue) {
      isOpen.value = newValue;
      if (detailsRef.value) {
        detailsRef.value.open = newValue;
      }
    }
  }
);

defineExpose({
  toggle: () => {
    if (detailsRef.value) {
      detailsRef.value.open = !detailsRef.value.open;
    }
  },
});
</script>

<style lang="scss" scoped>
@import "../../scss/main.scss";

.st-expandable-details {
  color: var(--text-color-primary);

  &__summary {
    display: flex;
    align-items: center;
    cursor: pointer;
    padding: rem-calc(8);
    list-style: none;
    gap: rem-calc(8);
    user-select: none;

    // Remove default marker/arrow in all browsers
    &::-webkit-details-marker {
      display: none;
    }

    &::marker {
      display: none;
    }

    &:hover {
      .st-expandable-details__heading {
        text-decoration: underline;
      }
    }

    &:focus {
      outline: none;
      box-shadow: 0 0 0 rem-calc(2) var(--border-color-quaternary);
      border-radius: rem-calc(4);
    }

    &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 rem-calc(2) var(--border-color-quaternary);
      border-radius: rem-calc(4);
    }
  }

  &__icon {
    flex-shrink: 0;
  }

  &__heading {
    font-family: var(--font-primary);
    font-size: rem-calc(16);
    font-weight: 600;
    margin: 0;
    flex: 1;
  }

  &__content {
    padding: rem-calc(8);
    color: var(--text-color-primary);
    font-family: var(--font-primary);
    line-height: 1.5;
  }
}
</style>
